/**
 * HelcimPaymentsModal
 * - BasePageWithHeader with transparent toolbar
 * - IonContent + inner IonGrid scroll container
 * - IonFab morphing search overlay (same pattern as HelcimSubscriptionsModal)
 * - Filter bar: status / payment mode / date range pill triggers, each opening
 *   an anchored PortalDropdown (same convention as InvoiceList.tsx)
 * - Transaction cards: amount, customer, card, date, status
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { dbContacts } from '../auth/firebase';
import {
  IonModal,
  IonPage,
  IonButton,
  IonContent,
  IonIcon,
  IonBadge,
  IonSpinner,
  IonSkeletonText,
  IonText,
  IonSearchbar,
  IonFab,
  IonGrid,
  IonList,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonRefresherContent,
} from '@ionic/react';
import {
  searchOutline,
  closeOutline,
  cashOutline,
  calendarOutline,
  chevronBackOutline,
  chevronForwardOutline,
  filterOutline,
} from 'ionicons/icons';
import { FaUniversity } from 'react-icons/fa';
import { Keyboard } from '@capacitor/keyboard';
import { KEYBOARD_GAP_PX } from '../hooks/useKeyboardHeight';
import BasePageWithHeader from './BasePageWithHeader';
import HelcimTransactionDetailModal from './HelcimTransactionDetailModal';
import PositionedRefresher from './PositionedRefresher';
import { applySearchOverlayPosition } from '../utils/overlayPosition';
import { getHelcimTransactions, getHelcimAchTransactions } from '../services/helcimInvoicingService';
import { getZohoInvoices, getBrevoReceiptStatuses, type BrevoInvoiceEmailStatus } from '../services/zohoInvoicingService';
import { brevoEmailState } from '../utils/brevoEmailState';
import Invoice from '../pages/Invoice';
import styles from './HelcimPaymentsModal.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { intlLocale, weekdayNames } from '../i18n/locale';
import { translateDynamic } from '../i18n/dynamic';
import { useSheetScrollGuard } from '../hooks/useSheetScrollGuard';

type DateRange = 'all' | 'week' | 'month' | 'last_month' | 'last_3_months' | 'custom';
type StatusOption = 'approved' | 'declined' | 'voided' | 'refunded' | 'pending' | 'processing';
type TypeOption = 'purchase' | 'preauth' | 'verify' | 'withdrawal';
type MethodOption = 'card' | 'ach';
type FilterCategory = 'amount' | 'status' | 'method' | 'type';

const PAGE_SIZE = 75;

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  all: 'All',
  get week() { return i18n.t('components.HelcimPaymentsModal.thisWeek'); },
  get month() { return i18n.t('components.HelcimPaymentsModal.thisMonth'); },
  get last_month() { return i18n.t('components.HelcimPaymentsModal.lastMonth'); },
  get last_3_months() { return i18n.t('components.HelcimPaymentsModal.last3Months'); },
  get custom() { return i18n.t('components.HelcimPaymentsModal.custom'); },
};

const STATUS_OPTION_LABELS: Record<StatusOption, { label: string; sub: string }> = {
  approved: { get label() { return i18n.t('components.HelcimPaymentsModal.successful'); }, sub: 'The transaction went through and the payment was completed.' },
  declined: { get label() { return i18n.t('components.HelcimPaymentsModal.declined'); }, sub: 'The bank or card issuer did not approve the payment.' },
  voided: { get label() { return i18n.t('components.HelcimPaymentsModal.voided'); }, sub: 'The transaction was canceled before it was settled.' },
  refunded: { get label() { return i18n.t('components.HelcimPaymentsModal.refunded'); }, sub: 'The full payment amount was returned to the customer.' },
  pending: { get label() { return i18n.t('components.HelcimPaymentsModal.pending'); }, sub: 'The ACH/EFT transaction has been submitted and is awaiting PAD confirmation.' },
  processing: { get label() { return i18n.t('components.HelcimPaymentsModal.processing'); }, sub: "The bank is processing the transaction and it hasn't cleared yet." },
};

const TYPE_OPTION_LABELS: Record<TypeOption, string> = {
  get purchase() { return i18n.t('components.HelcimPaymentsModal.purchase'); },
  get preauth() { return i18n.t('components.HelcimPaymentsModal.preAuthorization'); },
  get verify() { return i18n.t('components.HelcimPaymentsModal.verify'); },
  get withdrawal() { return i18n.t('components.HelcimPaymentsModal.withdrawal'); },
};

const METHOD_OPTION_LABELS: Record<MethodOption, string> = {
  get card() { return i18n.t('components.HelcimPaymentsModal.card'); },
  get ach() { return i18n.t('components.HelcimPaymentsModal.achEft'); },
};

const STATUS_COLOR: Record<string, string> = {
  approved: 'success',
  declined: 'danger',
  refunded: 'warning',
  reversed: 'medium',
  voided: 'medium',
  pending: 'tertiary',
  processing: 'tertiary',
};

// Tone-class lookup for the badge — ion-badge's default (non `.ion-color`)
// :host rule reads --background/--color directly, so a CSS-module tone class
// is applied instead of Ionic's `color` prop, matching InvoiceList's/
// BookingsListModal's badge system.
const BADGE_TONE_CLASS: Record<string, string> = {
  success: 'badgeSuccess',
  danger: 'badgeDanger',
  warning: 'badgeWarning',
  medium: 'badgeMedium',
  tertiary: 'badgeTertiary',
};

function resolveStatus(tx: Record<string, unknown>): string {
  const type = String(tx['type'] ?? tx['transactionType'] ?? tx['cardTransactionType'] ?? '').toLowerCase();
  const status = String(tx['status'] ?? '').toLowerCase();
  if (type === 'refund' || type.includes('refund')) return 'refunded';
  if (type === 'reverse' || type.includes('reverse') || type.includes('void')) return 'voided';
  if (status === 'declined') return 'declined';
  return status;
}

// For the Status filter's multi-select checkboxes — a stricter, closed-set
// version of resolveStatus() (left untouched since the row rendering/badge
// depend on its exact current output). Only buckets raw Helcim data can
// actually produce are included — no dead checkboxes that could never match.
function resolveStatusExtended(tx: Record<string, unknown>): StatusOption | null {
  const status = resolveStatus(tx);
  if (status === 'approved' || status === 'declined' || status === 'voided' || status === 'refunded') return status;
  if (status === 'pending' || status === 'processing') return status;
  return null;
}

// For the Type filter's multi-select checkboxes. Card transactions use
// purchase|preauth|verify|withdrawal; ACH transactions (normalizeAch()) use
// withdrawal|deposit|reversal — ACH's "withdrawal" (transactionType=1, the
// normal debit) is the one concept both share, so it matches the same
// Withdrawal checkbox as a card withdrawal. ACH deposit/reversal have no
// card-side equivalent and resolve to null, which excludes them whenever any
// Type filter is active (a reversal is never a "Verify", etc.).
function resolveTypeOption(tx: Record<string, unknown>): TypeOption | null {
  const type = pick(tx, 'type', 'transactionType', 'cardTransactionType').toLowerCase();
  if (type === 'purchase' || type === 'preauth' || type === 'verify' || type === 'withdrawal') return type;
  return null;
}

// Helcim's /ach/transactions endpoint returns numeric codes for status/currency
// (unlike /card-transactions, which returns strings). Map them to the same
// string shape the rest of this component expects.
const ACH_STATUS: Record<number, string> = { 1: 'approved', 2: 'declined', 4: 'cancelled', 5: 'pending' };
const ACH_CURRENCY: Record<number, string> = { 1: 'CAD', 2: 'USD' };

function normalizeAch(tx: Record<string, unknown>): Record<string, unknown> {
  const statusAuth = Number(tx['statusAuth'] ?? 0);
  const statusClearing = Number(tx['statusClearing'] ?? 0);
  // statusAuth=1 alone only means the debit was authorized — the batch still
  // needs to clear/settle (statusClearing=1) before it's truly "approved".
  // An authorized-but-not-yet-cleared transaction shows as pending instead.
  const resolvedStatus = statusAuth === 1 ? (statusClearing === 1 ? 'approved' : 'pending') : (ACH_STATUS[statusAuth] ?? 'pending');
  const currencyRaw = tx['currency'];
  const currency = typeof currencyRaw === 'number'
    ? (ACH_CURRENCY[currencyRaw] ?? 'USD')
    : (/^[A-Z]{3}$/.test(String(currencyRaw ?? '').trim().toUpperCase())
        ? String(currencyRaw).trim().toUpperCase()
        : 'USD');
  const l4 = String(tx['bankAccountL4L4'] ?? '').trim();
  const holderName = String(
    tx['cardHolderName'] ?? tx['bankAccountHolderName'] ?? tx['accountHolderName'] ??
    tx['customerName'] ?? tx['holderName'] ?? tx['name'] ?? '',
  );
  const displayName = holderName || (l4 ? i18n.t('components.HelcimPaymentsModal.bank', { l4 }) : '');
  // Helcim's own dashboard labels these Withdrawal (money pulled from the customer's bank —
  // the normal case) / Deposit (transactionType=2, money returned to the customer) /
  // Reversal (transactionType=4) — matching that wording exactly rather than card's
  // Purchase/Preauth/Verify, since ACH transactions are never any of those.
  const achTransactionType = Number(tx['transactionType'] ?? 0);
  const type = achTransactionType === 2 ? 'deposit' : achTransactionType === 4 ? 'reversal' : 'withdrawal';
  return {
    ...tx,
    transactionId: tx['id'],
    invoiceNumber: tx['invoiceNumber'] ?? tx['orderId'] ?? '',
    status: resolvedStatus,
    type,
    currency,
    ...(displayName ? { cardHolderName: displayName } : {}),
  };
}

// ── Card brand logos ──────────────────────────────────────────────────────────
const LOGO_STYLE: React.CSSProperties = { display: 'block', width: 34, height: 20, borderRadius: 3 };

const VisaLogo = () => {
  const { t: tr } = useTranslation();
  return (
  <img src="/Visa_Brandmark_Blue_RGB_2021.svg" alt={tr('components.HelcimPaymentsModal.visa')} style={{ ...LOGO_STYLE, objectFit: 'contain', background: 'white', border: '0.5px solid rgba(0,0,0,0.12)' }} />
);
};

// Two theme-specific badges stacked and toggled via the global (non-module)
// .mastercardLogoDark/.mastercardLogoLight rules in theme.scss — a
// CSS-module :global() selector doesn't reliably reach hashed classnames,
// and a JS classList check only runs once at initial render since this list
// has no React state tied to the theme toggle. A plain global stylesheet
// rule keyed off html.dark repaints instantly with the toggle, no JS needed.
// display is excluded from the shared LOGO_STYLE spread here since an inline
// style always beats a CSS class rule, which would otherwise make the
// display:none/block toggle below a no-op.
const MASTERCARD_LOGO_STYLE: React.CSSProperties = { ...LOGO_STYLE, display: undefined };
const MastercardLogo = () => {
  const { t: tr } = useTranslation();
  return (
  <>
    <img src="/ma_symbol.svg" alt={tr('components.HelcimPaymentsModal.mastercard')} className="mastercardLogoDark" style={{ ...MASTERCARD_LOGO_STYLE, objectFit: 'contain', background: '#252525' }} />
    <img src="/ma_symbol-lt.svg" alt={tr('components.HelcimPaymentsModal.mastercard')} className="mastercardLogoLight" style={{ ...MASTERCARD_LOGO_STYLE, objectFit: 'contain', background: 'white', border: '0.5px solid rgba(0,0,0,0.12)' }} />
  </>
);
};

const AmexLogo = () => {
  const { t: tr } = useTranslation();
  return (
  <img src="/AXP.svg" alt={tr('components.HelcimPaymentsModal.americanExpress')} style={{ ...LOGO_STYLE, objectFit: 'cover' }} />
);
};

const DiscoverLogo = () => {
  const { t: tr } = useTranslation();
  return (
  <svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" style={{ ...LOGO_STYLE, border: '0.5px solid rgba(0,0,0,0.1)' }}>
    <defs>
      <linearGradient id="discGrad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="50%" stopColor="white" />
        <stop offset="100%" stopColor="#F76F20" />
      </linearGradient>
    </defs>
    <rect width="38" height="24" rx="4" fill="url(#discGrad)" />
    <circle cx="29" cy="12" r="7" fill="#F76F20" />
    <text x="3" y="14" fontFamily="Arial, sans-serif" fontSize="5.5" fontWeight="800" fill="#333" letterSpacing="0.2">{tr('components.HelcimPaymentsModal.discover')}</text>
  </svg>
);
};

const GenericCardLogo = () => (
  <svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" style={LOGO_STYLE}>
    <rect width="38" height="24" rx="4" fill="rgba(120,120,128,0.2)" stroke="rgba(120,120,128,0.3)" strokeWidth="1" />
    <rect x="4" y="7" width="12" height="8" rx="2" fill="rgba(120,120,128,0.5)" />
    <rect x="4" y="16" width="6" height="2" rx="1" fill="rgba(120,120,128,0.4)" />
    <rect x="12" y="16" width="6" height="2" rx="1" fill="rgba(120,120,128,0.4)" />
  </svg>
);

// Same footprint (38×24 viewBox, LOGO_STYLE dimensions) as the card brand
// logos, so an ACH/EFT row carries the same visual weight as a Visa/
// Mastercard/Amex badge instead of a bare icon floating next to text. Not a
// real brand mark, so its color is free to adapt per theme — two badges
// stacked and toggled via the global .bankLogoDark/.bankLogoLight rules in
// theme.scss, same proven pattern as MastercardLogo above.
const BANK_LOGO_STYLE: React.CSSProperties = { ...LOGO_STYLE, display: undefined };
const BankLogo = () => (
  <>
    <svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" className="bankLogoDark" style={{ ...BANK_LOGO_STYLE, background: '#2f5233' }}>
      <rect width="38" height="24" rx="4" fill="#2f5233" />
      <foreignObject x="9" y="4" width="20" height="16">
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 13 }}>
          <FaUniversity />
        </div>
      </foreignObject>
    </svg>
    <svg viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" className="bankLogoLight" style={{ ...BANK_LOGO_STYLE, background: '#3d7a4d' }}>
      <rect width="38" height="24" rx="4" fill="#3d7a4d" />
      <foreignObject x="9" y="4" width="20" height="16">
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 13 }}>
          <FaUniversity />
        </div>
      </foreignObject>
    </svg>
  </>
);

function CardBrandLogo({ brand }: { brand: string }) {
  const b = brand.toLowerCase().replace(/\s/g, '');
  // Helcim 2-letter codes: VI, MC, AX/AM, DI + full names
  if (b === 'vi' || b === 'visa') return <VisaLogo />;
  if (b === 'mc' || b === 'mastercard') return <MastercardLogo />;
  if (b === 'ax' || b === 'am' || b === 'amex' || b === 'americanexpress') return <AmexLogo />;
  if (b === 'di' || b === 'disc' || b === 'discover') return <DiscoverLogo />;
  return <GenericCardLogo />;
}

// ── Card number formatter ─────────────────────────────────────────────────────
function pick(tx: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = String(tx[k] ?? '').trim();
    if (v) return v;
  }
  // Also try nested tx.card object
  const card = tx['card'] as Record<string, unknown> | undefined;
  if (card && typeof card === 'object') {
    for (const k of keys) {
      const v = String(card[k] ?? '').trim();
      if (v) return v;
    }
  }
  return '';
}

function formatCardNumber(tx: Record<string, unknown>): string {
  const raw = pick(tx, 'cardNumber', 'card_number', 'maskedPan', 'pan');
  if (raw.length === 10) {
    // Helcim format: first-6 + last-4 concatenated e.g. "4000000028"
    const f6 = raw.slice(0, 6);
    const l4 = raw.slice(6);
    return `${f6.slice(0, 4)} ${f6.slice(4, 6)}** **** ${l4}`;
  }
  if (raw.length >= 12) {
    // Fully masked number e.g. "400000XXXXXX0028"
    const clean = raw.replace(/[^0-9X*x]/gi, '');
    if (clean.length >= 15) {
      const f6 = clean.slice(0, 6);
      const l4 = clean.slice(-4);
      return `${f6.slice(0, 4)} ${f6.slice(4, 6)}** **** ${l4}`;
    }
  }
  // Separate first-6 + last-4 fields
  const f6 = pick(tx, 'cardF6', 'card_f6', 'cardBin', 'card_bin', 'cardFirst6', 'first6');
  const l4 = pick(tx, 'cardSuffix', 'card_suffix', 'cardL4', 'card_l4', 'cardLast4', 'last4', 'cardEnd');
  if (f6.length >= 6 && l4.length >= 4) {
    return `${f6.slice(0, 4)} ${f6.slice(4, 6)}** **** ${l4}`;
  }
  if (l4.length >= 4) return `**** **** **** ${l4}`;
  return '—';
}

function getDateRange(range: DateRange, custom?: { start: string; end: string }): { dateStart: string; dateEnd: string } {
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  if (range === 'custom' && custom?.start && custom?.end) {
    return { dateStart: custom.start, dateEnd: custom.end };
  }
  if (range === 'all' || range === 'custom') {
    return { dateStart: '2015-01-01', dateEnd: fmt(now) };
  }
  if (range === 'week') {
    const start = new Date(now);
    start.setDate(now.getDate() - 7);
    return { dateStart: fmt(start), dateEnd: fmt(now) };
  }
  if (range === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { dateStart: fmt(start), dateEnd: fmt(now) };
  }
  if (range === 'last_3_months') {
    const start = new Date(now);
    start.setMonth(now.getMonth() - 3);
    return { dateStart: fmt(start), dateEnd: fmt(now) };
  }
  // last_month
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return { dateStart: fmt(start), dateEnd: fmt(end) };
}

// ACH transactions are fetched unfiltered (see loadTransactions) so the date-range
// picker filters them client-side instead — mirrors desktop's isInDateRange().
function isInDateRange(tx: Record<string, unknown>, range: DateRange, custom?: { start: string; end: string }): boolean {
  if (range === 'all') return true;
  const raw = tx['dateCreated'] ?? tx['dateTime'] ?? tx['date'];
  if (!raw) return true;
  const d = new Date(String(raw).replace(' ', 'T'));
  if (isNaN(d.getTime())) return true;
  const { dateStart, dateEnd } = getDateRange(range, custom);
  return d >= new Date(dateStart + 'T00:00:00') && d <= new Date(dateEnd + 'T23:59:59');
}

function fmtCurrency(amount: unknown, currency = 'CAD') {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: String(currency || 'CAD'),
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
  }).format(Number(amount ?? 0));
}

function fmtDate(dateStr: unknown): string {
  if (!dateStr) return '—';
  const d = new Date(String(dateStr).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
}

// Transaction type shown under the amount: the four filterable types already have
// translated labels; anything else (deposit, reversal, refund…) comes from the dynamic pack.
function txTypeLabel(type: string): string {
  if (type in TYPE_OPTION_LABELS) return TYPE_OPTION_LABELS[type as TypeOption];
  return translateDynamic(type.charAt(0).toUpperCase() + type.slice(1));
}

interface FilterDraft {
  amountMin: string;
  amountMax: string;
  dateRange: DateRange;
  customDateStart: string;
  customDateEnd: string;
  statusFilter: Set<StatusOption>;
  methodFilter: Set<MethodOption>;
  typeFilter: Set<TypeOption>;
}

// Small month-grid calendar for manual start/end date selection — forked from
// the desktop PaymentsDesktop.tsx version (plain Date arithmetic, no library).
// First tap sets the start date; the next tap sets the end (swapping if it
// lands before the start) and highlights the range between them.
const FilterCalendar: React.FC<{
  start: string;
  end: string;
  onChange: (start: string, end: string) => void;
}> = ({ start, end, onChange }) => {
  const [viewMonth, setViewMonth] = useState(() => {
    const base = start ? new Date(start + 'T00:00:00') : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const startD = start ? new Date(start + 'T00:00:00') : null;
  const endD = end ? new Date(end + 'T00:00:00') : null;

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = firstDay.getDay();
  const cells: (Date | null)[] = [
    ...Array(leadingBlanks).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
  ];

  const handleDayClick = (d: Date) => {
    if (!startD || (startD && endD)) {
      onChange(fmt(d), '');
    } else if (d < startD) {
      onChange(fmt(d), fmt(startD));
    } else {
      onChange(fmt(startD), fmt(d));
    }
  };

  return (
    <div className={styles.filterCalendar}>
      <div className={styles.filterCalendarHeader}>
        <button type="button" onClick={() => setViewMonth(new Date(year, month - 1, 1))}>
          <IonIcon icon={chevronBackOutline} />
        </button>
        <span>{viewMonth.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' })}</span>
        <button type="button" onClick={() => setViewMonth(new Date(year, month + 1, 1))}>
          <IonIcon icon={chevronForwardOutline} />
        </button>
      </div>
      <div className={styles.filterCalendarGrid}>
        {weekdayNames('short').map(d => (
          <div key={d} className={styles.filterCalendarDow}>{d}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`blank-${i}`} />;
          const isStart = startD && fmt(d) === fmt(startD);
          const isEnd = endD && fmt(d) === fmt(endD);
          const inRange = startD && endD && d > startD && d < endD;
          return (
            <button
              type="button"
              key={fmt(d)}
              className={`${styles.filterCalendarDay} ${isStart || isEnd ? styles.filterCalendarDaySelected : ''} ${inRange ? styles.filterCalendarDayInRange : ''}`}
              onClick={() => handleDayClick(d)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Standalone sheet for "Select Dates" — independent of the Filters accordion
// sheet, not one of its rows. Same staged-draft/Reset/Apply model.
const DateSheet: React.FC<{
  isOpen: boolean;
  draft: FilterDraft;
  setDraft: React.Dispatch<React.SetStateAction<FilterDraft>>;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
}> = ({ isOpen, draft, setDraft, onClose, onApply, onReset }) => {
  const { t: tr } = useTranslation();
  const sheetBodyRef = useSheetScrollGuard();
  return (
  <IonModal
    isOpen={isOpen}
    onDidDismiss={onClose}
    initialBreakpoint={1}
    breakpoints={[0, 1]}
    className={styles.filterSheetModal}
  >
    <div className={`${styles.filterSheetHeader} ${styles.filterSheetHeaderNoBorder}`}>
      <div className={styles.filterSheetHeaderSpacer} />
      <span>{tr('components.HelcimPaymentsModal.filterByDate')}</span>
      <IonButton fill="clear" onClick={onClose} className={styles.filterSheetCloseBtn}>
        <IonIcon icon={closeOutline} />
      </IonButton>
    </div>

    <div ref={sheetBodyRef} className={styles.filterSheetBody}>
      <div className={styles.filterSheetSection}>
        <div className={styles.filterQuickPicks}>
          {(['week', 'month', 'last_3_months'] as DateRange[]).map(r => (
            <button
              key={r}
              type="button"
              className={`${styles.filterQuickPick} ${draft.dateRange === r ? styles.filterQuickPickActive : ''}`}
              onClick={() => setDraft(d => ({ ...d, dateRange: r, customDateStart: '', customDateEnd: '' }))}
            >
              {DATE_RANGE_LABELS[r]}
            </button>
          ))}
        </div>
        <div className={styles.filterDateInputs}>
          <label>{tr('components.HelcimPaymentsModal.startDate')}
            <input
              type="text"
              placeholder={tr('components.HelcimPaymentsModal.mmDdYyyy')}
              value={draft.customDateStart}
              onChange={e => setDraft(d => ({ ...d, dateRange: 'custom', customDateStart: e.target.value }))}
            />
          </label>
          <IonIcon icon={chevronForwardOutline} className={styles.filterDateArrow} />
          <label>{tr('components.HelcimPaymentsModal.endDate')}
            <input
              type="text"
              placeholder={tr('components.HelcimPaymentsModal.mmDdYyyy')}
              value={draft.customDateEnd}
              onChange={e => setDraft(d => ({ ...d, dateRange: 'custom', customDateEnd: e.target.value }))}
            />
          </label>
        </div>
        <FilterCalendar
          start={draft.customDateStart}
          end={draft.customDateEnd}
          onChange={(s, e) => setDraft(d => ({ ...d, dateRange: 'custom', customDateStart: s, customDateEnd: e }))}
        />
      </div>
    </div>

    <div className={styles.filterSheetFooter}>
      <button type="button" className={styles.filterSheetResetBtn} onClick={onReset}>{tr('components.HelcimPaymentsModal.reset')}
      </button>
      <button type="button" className={styles.filterSheetApplyBtn} onClick={onApply}>{tr('components.HelcimPaymentsModal.apply')}
      </button>
    </div>
  </IonModal>
);
};

const FILTER_CATEGORIES: { key: FilterCategory; label: string }[] = [
  { key: 'amount', get label() { return i18n.t('components.HelcimPaymentsModal.amount'); } },
  { key: 'status', get label() { return i18n.t('components.HelcimPaymentsModal.status'); } },
  { key: 'method', get label() { return i18n.t('components.HelcimPaymentsModal.method2'); } },
  { key: 'type', get label() { return i18n.t('components.HelcimPaymentsModal.type'); } },
];

// One sheet, one scrollable accordion list — tapping a category's row
// expands its controls inline right below it (chevron flips), collapsing
// whichever other category was open. Matches Helcim's own mobile app
// pattern: no back-button navigation between separate screens.
const FilterSheet: React.FC<{
  isOpen: boolean;
  expandedCategory: FilterCategory | null;
  setExpandedCategory: React.Dispatch<React.SetStateAction<FilterCategory | null>>;
  draft: FilterDraft;
  setDraft: React.Dispatch<React.SetStateAction<FilterDraft>>;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
}> = ({ isOpen, expandedCategory, setExpandedCategory, draft, setDraft, onClose, onApply, onReset }) => {
  const { t: tr } = useTranslation();
  const sheetBodyRef = useSheetScrollGuard();
  const toggleInSet = <T,>(set: Set<T>, value: T): Set<T> => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    return next;
  };

  const renderCategoryContent = (key: FilterCategory) => {
    if (key === 'amount') {
      return (
        <div className={styles.filterSheetSection}>
          <div className={styles.amountInputRow}>
            <label>{tr('components.HelcimPaymentsModal.min')}
              <div className={styles.amountInputWrap}>
                <span>$</span>
                <input type="number" value={draft.amountMin} onChange={e => setDraft(d => ({ ...d, amountMin: e.target.value }))} placeholder="0" />
              </div>
            </label>
            <label>{tr('components.HelcimPaymentsModal.max')}
              <div className={styles.amountInputWrap}>
                <span>$</span>
                <input type="number" value={draft.amountMax} onChange={e => setDraft(d => ({ ...d, amountMax: e.target.value }))} placeholder="0" />
              </div>
            </label>
          </div>
        </div>
      );
    }
    if (key === 'status') {
      return (
        <IonList lines="full" className={styles.filterSheetSection}>
          {(Object.keys(STATUS_OPTION_LABELS) as StatusOption[]).map(opt => (
            <IonItem key={opt} lines="full" className={styles.filterCheckboxRow}>
              <IonCheckbox
                slot="start"
                className={styles.filterCheckbox}
                checked={draft.statusFilter.has(opt)}
                onIonChange={() => setDraft(d => ({ ...d, statusFilter: toggleInSet(d.statusFilter, opt) }))}
              />
              <IonLabel className="ion-text-wrap">
                <h3 className={styles.filterCheckboxLabel}>{STATUS_OPTION_LABELS[opt].label}</h3>
                <p className={styles.filterCheckboxSub}>{STATUS_OPTION_LABELS[opt].sub}</p>
              </IonLabel>
            </IonItem>
          ))}
        </IonList>
      );
    }
    if (key === 'method') {
      return (
        <IonList lines="full" className={styles.filterSheetSection}>
          {(Object.keys(METHOD_OPTION_LABELS) as MethodOption[]).map(opt => (
            <IonItem key={opt} lines="full" className={styles.filterCheckboxRow}>
              <IonCheckbox
                slot="start"
                className={styles.filterCheckbox}
                checked={draft.methodFilter.has(opt)}
                onIonChange={() => setDraft(d => ({ ...d, methodFilter: toggleInSet(d.methodFilter, opt) }))}
              />
              <IonLabel className={styles.filterCheckboxLabel}>{METHOD_OPTION_LABELS[opt]}</IonLabel>
            </IonItem>
          ))}
        </IonList>
      );
    }
    // type
    return (
      <IonList lines="full" className={styles.filterSheetSection}>
        {(Object.keys(TYPE_OPTION_LABELS) as TypeOption[]).map(opt => (
          <IonItem key={opt} lines="full" className={styles.filterCheckboxRow}>
            <IonCheckbox
              slot="start"
              className={styles.filterCheckbox}
              checked={draft.typeFilter.has(opt)}
              onIonChange={() => setDraft(d => ({ ...d, typeFilter: toggleInSet(d.typeFilter, opt) }))}
            />
            <IonLabel className={styles.filterCheckboxLabel}>{TYPE_OPTION_LABELS[opt]}</IonLabel>
          </IonItem>
        ))}
      </IonList>
    );
  };

  const categoryBadge = (key: FilterCategory): string | null => {
    if (key === 'amount') {
      if (!draft.amountMin && !draft.amountMax) return null;
      return `$${draft.amountMin || '0'} - ${draft.amountMax || 'Any'}`;
    }
    if (key === 'status') return draft.statusFilter.size > 0 ? String(draft.statusFilter.size) : null;
    if (key === 'method') return draft.methodFilter.size > 0 ? String(draft.methodFilter.size) : null;
    return draft.typeFilter.size > 0 ? String(draft.typeFilter.size) : null;
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
      className={styles.filterSheetModal}
    >
      <div ref={sheetBodyRef} className={styles.filterSheetBody}>
        <IonList lines="full" className={styles.filterCategoryList}>
          {FILTER_CATEGORIES.map((c, i) => {
            const isExpanded = expandedCategory === c.key;
            const isLast = i === FILTER_CATEGORIES.length - 1;
            const badge = categoryBadge(c.key);
            return (
              <React.Fragment key={c.key}>
                <IonItem
                  button
                  detail={false}
                  lines={isLast && !isExpanded ? 'none' : 'full'}
                  className={styles.filterCategoryItem}
                  onClick={() => setExpandedCategory(prev => (prev === c.key ? null : c.key))}
                >
                  <IonLabel>{c.label}</IonLabel>
                  {badge && <span className={styles.filterCategoryBadge}>{badge}</span>}
                  <IonIcon icon={chevronForwardOutline} slot="end" className={`${styles.filterCategoryChevron} ${isExpanded ? styles.filterCategoryChevronOpen : ''}`} />
                </IonItem>
                {isExpanded && renderCategoryContent(c.key)}
              </React.Fragment>
            );
          })}
        </IonList>
      </div>

      <div className={styles.filterSheetFooter}>
        <button type="button" className={styles.filterSheetResetBtn} onClick={onReset}>{tr('components.HelcimPaymentsModal.reset')}
        </button>
        <button type="button" className={styles.filterSheetApplyBtn} onClick={onApply}>{tr('components.HelcimPaymentsModal.apply')}
        </button>
      </div>
    </IonModal>
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  asPage?: boolean;
}

const HelcimPaymentsModal: React.FC<Props> = ({ isOpen, onClose, asPage = false }) => {
  const { t: tr } = useTranslation();
  // Merged card+ACH transaction list. Each source keeps its own internal
  // pagination/in-flight/hasMore tracking (both now support real server-side
  // date filtering — see getDateRange/loadCard/loadAch below), but the UI
  // only ever sees one combined, sorted `transactions` array. methodFilter
  // is a client-side filter over that array, not a switch between two
  // separate data sources — this matches how Helcim's own merchant
  // dashboard presents card + ACH transactions together.
  const [transactions, setTransactions] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = useRef(false);

  const cardPageRef = useRef(1);
  const cardInFlightRef = useRef(false);
  const cardRequestId = useRef(0);
  const cardHasMoreRef = useRef(false);

  const achPageRef = useRef(1);
  const achInFlightRef = useRef(false);
  const achRequestId = useRef(0);
  const achHasMoreRef = useRef(false);

  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(true);
  const [selectedTx, setSelectedTx] = useState<Record<string, unknown> | null>(null);
  const [invoiceIdToOpen, setInvoiceIdToOpen] = useState<string | null>(null);
  const [refresherDisabled, setRefresherDisabled] = useState(false);

  // Two independent sheets: DateSheet (its own "Select Dates" pill, no
  // accordion) and FilterSheet (its own "Filters" pill, one scrollable
  // accordion list — Amount/Status/Method/Type — tapping a row expands its
  // controls inline below it, collapsing whichever other row was open).
  const [isDateSheetOpen, setIsDateSheetOpen] = useState(false);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<FilterCategory | null>(null);
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [statusFilter, setStatusFilter] = useState<Set<StatusOption>>(new Set());
  const [methodFilter, setMethodFilter] = useState<Set<MethodOption>>(new Set());
  const [typeFilter, setTypeFilter] = useState<Set<TypeOption>>(new Set());
  // Staged draft, seeded from applied values whenever the sheet opens — nothing
  // in the visible list changes until "Apply" is tapped.
  const [draft, setDraft] = useState({
    amountMin: '', amountMax: '',
    dateRange: 'all' as DateRange, customDateStart: '', customDateEnd: '',
    statusFilter: new Set<StatusOption>(),
    methodFilter: new Set<MethodOption>(),
    typeFilter: new Set<TypeOption>(),
  });

  // customerCode -> contact name, from our own `contacts` collection (every
  // contact record has a customerCode). cardHolderName is whatever name was
  // on the card at swipe time — someone other than the actual customer can
  // pay (e.g. a spouse) — so it's never a reliable way to identify whose
  // transaction this is and must not be used for the title. The whole
  // collection is fetched once per modal open (same pattern ContactsList.tsx
  // uses for its own contact list, backed by dbContacts' persistent local
  // cache) rather than per-transaction, so there's no per-row fetch/flash.
  const [customerNames, setCustomerNames] = useState<Record<string, string>>({});
  const customerNamesLoadedRef = useRef(false);

  // customerCode -> contact email (same contacts fetch), used to find the receipt email in Brevo.
  const [customerEmails, setCustomerEmails] = useState<Record<string, string>>({});
  // Status of each payment's "Payment Received" email, looked up live from Brevo — not stored.
  const [receiptStatuses, setReceiptStatuses] = useState<Record<string, BrevoInvoiceEmailStatus>>({});
  const receiptAskedRef = useRef<Set<string>>(new Set());

  const handleInvoiceClick = async (invoiceNumber: string) => {
    console.log('[invoice click] invoiceNumber:', invoiceNumber);
    try {
      const results = await getZohoInvoices({ search_text: invoiceNumber });
      console.log('[invoice click] results:', results.length, results.map(r => r.invoice_number));
      const match = results.find(inv => inv.invoice_number === invoiceNumber) ?? results[0];
      if (match?.invoice_id) {
        console.log('[invoice click] opening invoice_id:', match.invoice_id);
        setInvoiceIdToOpen(match.invoice_id);
      } else {
        console.warn('[invoice click] no match found');
      }
    } catch (e) {
      console.error('[invoice click] error:', e);
    }
  };

  const overlayRef = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const scrollRef = useRef<HTMLIonGridElement>(null);
  const ionContentRef = useRef<HTMLIonContentElement>(null);
  const titleCardRef = useRef<HTMLDivElement>(null);
  const wasAtTop = useRef(true);
  const loadGenRef = useRef(0);

  // Merges new items from either source into the combined list: dedupes by
  // transactionId (cross-source collision isn't expected, but costs nothing
  // to guard) and re-sorts by date desc so card/ACH rows interleave
  // correctly regardless of which source they came from or when they
  // arrived relative to each other.
  const mergeInTransactions = useCallback((newItems: Record<string, unknown>[]) => {
    setTransactions(prev => {
      const existingIds = new Set(prev.map(t => String(t['transactionId'] ?? t['id'] ?? '')));
      const deduped = newItems.filter(t => !existingIds.has(String(t['transactionId'] ?? t['id'] ?? '')));
      const merged = [...prev, ...deduped];
      const txTime = (t: Record<string, unknown>): number => {
        const raw = t['dateCreated'] ?? t['dateTime'] ?? t['date'];
        if (!raw) return 0;
        const d = new Date(String(raw).replace(' ', 'T'));
        return isNaN(d.getTime()) ? 0 : d.getTime();
      };
      merged.sort((a, b) => txTime(b) - txTime(a));
      return merged;
    });
  }, []);

  // Both card and ACH now support real server-side date filtering (Helcim's
  // /card-transactions uses dateFrom/dateTo, /ach/transactions uses
  // startDate/endDate — both already translated from our dateStart/dateEnd
  // naming in shared/src/services/api/helcim/helcim.ts), so both sources
  // are fetched identically and merged into one list, filtered client-side
  // afterward by amount/method/status/type/search (date filtering happens
  // server-side; the client-side dateFiltered pass below is a safety net
  // in case a round trip doesn't apply it, same as before this merge).
  const fetchCardPage = useCallback(async (reset: boolean): Promise<boolean> => {
    const requestId = reset ? ++cardRequestId.current : cardRequestId.current;
    if (!reset && cardInFlightRef.current) return false;
    cardInFlightRef.current = true;
    try {
      const currentPage = reset ? 1 : cardPageRef.current + 1;
      const { dateStart, dateEnd } = getDateRange(dateRange, { start: customDateStart, end: customDateEnd });
      const result = await getHelcimTransactions({ dateStart, dateEnd, page: currentPage, limit: PAGE_SIZE });
      if (requestId !== cardRequestId.current) return false;
      cardPageRef.current = currentPage;
      cardHasMoreRef.current = result.hasMore;
      mergeInTransactions(result.transactions);
      return true;
    } catch (e) {
      console.error('[fetchCardPage] ERROR', e);
      return false;
    } finally {
      cardInFlightRef.current = false;
    }
  }, [dateRange, customDateStart, customDateEnd, mergeInTransactions]);

  const fetchAchPage = useCallback(async (reset: boolean): Promise<boolean> => {
    const requestId = reset ? ++achRequestId.current : achRequestId.current;
    if (!reset && achInFlightRef.current) return false;
    achInFlightRef.current = true;
    try {
      const currentPage = reset ? 1 : achPageRef.current + 1;
      const { dateStart, dateEnd } = getDateRange(dateRange, { start: customDateStart, end: customDateEnd });
      const result = await getHelcimAchTransactions({ dateStart, dateEnd, page: currentPage, limit: PAGE_SIZE });
      if (requestId !== achRequestId.current) return false;
      const txns = result.transactions.map(tx => normalizeAch(tx));
      achPageRef.current = currentPage;
      achHasMoreRef.current = result.hasMore;
      mergeInTransactions(txns);
      return true;
    } catch (e) {
      console.error('[fetchAchPage] ERROR', e);
      return false;
    } finally {
      achInFlightRef.current = false;
    }
  }, [dateRange, customDateStart, customDateEnd, mergeInTransactions]);

  const loadTransactions = useCallback(async () => {
    const loadId = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    cardPageRef.current = 1;
    achPageRef.current = 1;
    setTransactions([]);
    const [cardOk, achOk] = await Promise.allSettled([fetchCardPage(true), fetchAchPage(true)])
      .then(results => results.map(r => r.status === 'fulfilled' && r.value));
    // A newer load (e.g. the open effect and the date-range effect both firing on first open)
    // superseded this one — its fetches return false by design, so don't report that as a failure.
    if (loadId !== loadGenRef.current) return;
    loadedRef.current = true;
    setLoading(false);
    if (!cardOk && !achOk) {
      setError(tr('components.HelcimPaymentsModal.failedToLoadTransactions'));
    }
  }, [fetchCardPage, fetchAchPage, tr]);

  const loadMoreTransactions = useCallback(() => {
    // Reads *Ref.current (synchronous, updated the instant the fetch resolves) rather than
    // React state — state updates propagate through a render/effect cycle, which left a
    // window where a fast-firing scroll check could still see a just-superseded "hasMore:
    // true" and fire one redundant extra request. Only fetches from sources that still have
    // more — once a source is exhausted, further scrolling only touches the other.
    if (cardInFlightRef.current || achInFlightRef.current) return;
    if (!cardHasMoreRef.current && !achHasMoreRef.current) return;
    setLoadingMore(true);
    const fetches: Promise<boolean>[] = [];
    if (cardHasMoreRef.current) fetches.push(fetchCardPage(false));
    if (achHasMoreRef.current) fetches.push(fetchAchPage(false));
    Promise.allSettled(fetches).finally(() => setLoadingMore(false));
  }, [fetchCardPage, fetchAchPage]);

  // Same rAF-polling pattern as ChatScroller — only checks "near bottom" when scrollTop has
  // actually changed since the last frame. A plain scroll-event distance check (even with an
  // overflow guard) either fires spuriously right after switching tabs (short/just-loaded
  // content) or, with a guard, can block a genuine load-more if the loaded content doesn't
  // happen to overflow by much. Checking only on real scrollTop movement avoids both.
  // The loop itself runs once for the component's lifetime (mirroring ChatScroller) — it
  // reads the latest loadMoreTransactions via a ref instead of depending on it directly, so
  // the loop (and its lastScrollTop/lastTriggerTime state) never gets torn down and rebuilt
  // mid-load, which would otherwise make every load-state change look like a fresh scroll.
  const loadMoreTransactionsRef = useRef(loadMoreTransactions);
  useEffect(() => {
    loadMoreTransactionsRef.current = loadMoreTransactions;
  }, [loadMoreTransactions]);

  useEffect(() => {
    let rafId: number;
    let lastScrollTop = -1;
    let lastTriggerTime = 0;
    const checkScroll = () => {
      const el = scrollRef.current as unknown as HTMLElement | null;
      if (el && el.scrollTop !== lastScrollTop) {
        lastScrollTop = el.scrollTop;
        const deltaFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        const now = Date.now();
        if (deltaFromBottom < 300 && now - lastTriggerTime > 100) {
          lastTriggerTime = now;
          loadMoreTransactionsRef.current();
        }
      }
      rafId = requestAnimationFrame(checkScroll);
    };
    rafId = requestAnimationFrame(checkScroll);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Load once per modal open; reopening without closing just shows what's already loaded.
  // Deferred via a resolved-promise microtask (not called synchronously in
  // the effect body) — setRefresherDisabled/loadTransactions's own setState
  // calls are needed on every real invocation, so they can't just be
  // dropped the way a mount-only redundant reset could be.
  useEffect(() => {
    if (!isOpen) return;
    wasAtTop.current = true;
    if (scrollRef.current) (scrollRef.current as unknown as HTMLElement).scrollTop = 0;
    Promise.resolve().then(() => {
      setRefresherDisabled(false);
      if (!loadedRef.current) loadTransactions();
    });
  }, [isOpen, loadTransactions]);

  // Both sources are server-filtered by date range now, so a date-range change needs a
  // fresh fetch from both. Skip the initial mount — the effect above already handles the
  // first load once the modal opens.
  // Keyed on the date values themselves, not on isOpen/loadTransactions identity: those also
  // change on first open and on language change, which previously triggered a duplicate load.
  const dateKey = `${String(dateRange)}|${String(customDateStart)}|${String(customDateEnd)}`;
  const lastDateKeyRef = useRef(dateKey);
  useEffect(() => {
    if (lastDateKeyRef.current === dateKey) return;
    lastDateKeyRef.current = dateKey;
    // Changed while closed: mark stale so the open effect reloads on next open.
    if (!isOpen) {
      loadedRef.current = false;
      return;
    }
    Promise.resolve().then(() => loadTransactions());
  }, [dateKey, isOpen, loadTransactions]);

  // Both sources are expected to come back date-filtered from the server, but filtering
  // client-side here too means the visible list is always correct regardless of whether a
  // round trip actually applied the filter.
  const dateFiltered = transactions.filter(tx => isInDateRange(tx, dateRange, { start: customDateStart, end: customDateEnd }));

  const amountFiltered = dateFiltered.filter(tx => {
    const amt = Number(tx['amount'] ?? tx['totalAmount'] ?? 0);
    const min = amountMin.trim() ? Number(amountMin) : null;
    const max = amountMax.trim() ? Number(amountMax) : null;
    if (min !== null && amt < min) return false;
    if (max !== null && amt > max) return false;
    return true;
  });

  const methodFiltered = methodFilter.size === 0
    ? amountFiltered
    : amountFiltered.filter(tx => {
        const isAch = Boolean(tx['bankAccountId'] || tx['bankAccountL4L4']);
        return isAch ? methodFilter.has('ach') : methodFilter.has('card');
      });

  const typeAndStatusFiltered = methodFiltered.filter(tx => {
    if (statusFilter.size > 0) {
      const status = resolveStatusExtended(tx);
      if (!status || !statusFilter.has(status)) return false;
    }
    if (typeFilter.size > 0) {
      // resolveTypeOption() already unifies card and ACH: ACH withdrawal
      // matches the shared Withdrawal option, and anything unresolved
      // (ACH deposit/reversal, or a voided/reversed card "reverse") is
      // excluded rather than passed through.
      const type = resolveTypeOption(tx);
      if (!type || !typeFilter.has(type)) return false;
    }
    return true;
  });

  const filtered = typeAndStatusFiltered.filter(tx => {
    const q = searchText.toLowerCase().trim();
    if (!q) return true;
    const customerCode = String(tx['customerCode'] ?? '');
    return (
      customerCode.toLowerCase().includes(q) ||
      String(customerNames[customerCode] ?? '').toLowerCase().includes(q) ||
      String(tx['cardHolderName'] ?? '').toLowerCase().includes(q) ||
      String(tx['transactionId'] ?? '').toLowerCase().includes(q) ||
      String(tx['approvalCode'] ?? '').toLowerCase().includes(q) ||
      String(tx['invoiceNumber'] ?? '').toLowerCase().includes(q)
    );
  });

  // A search with only a few local matches never scrolls (short list =
  // nothing to scroll), so the rAF scroll-driven loader above never gets a
  // scrollTop change to react to and silently stops pulling in more pages —
  // a match sitting on a later page can never be found. While actively
  // searching with more pages available, keep loading until either enough
  // matches are found or both sources are exhausted.
  useEffect(() => {
    if (!searchText.trim()) return;
    if (filtered.length >= 20) return;
    if (!cardHasMoreRef.current && !achHasMoreRef.current) return;
    loadMoreTransactionsRef.current();
  }, [searchText, filtered.length]);

  // Load the full contacts collection once per modal open (same
  // load-once-on-open pattern as AddSubscriberModal's customer picker, and
  // the same collection/database ContactsList.tsx reads in full) — every
  // contact has a customerCode, so this is a single request covering every
  // transaction's customer instead of a fetch per transaction.
  useEffect(() => {
    if (!isOpen || customerNamesLoadedRef.current) return;
    customerNamesLoadedRef.current = true;
    getDocs(collection(dbContacts, 'contacts'))
      .then(snap => {
        const map: Record<string, string> = {};
        const emailMap: Record<string, string> = {};
        snap.forEach(doc => {
          const d = doc.data() as Record<string, unknown>;
          const code = String(d['customerCode'] ?? '').trim();
          if (!code) return;
          const first = String(d['firstName'] ?? '').trim();
          const last = String(d['lastName'] ?? '').trim();
          const name = String(d['name'] ?? '').trim() || `${first} ${last}`.trim();
          if (name) map[code] = name;
          const email = String(d['email'] ?? d['primaryEmail'] ?? '').trim();
          if (email) emailMap[code] = email;
        });
        console.log('[customerNames] contacts=%d resolved=%d', snap.size, Object.keys(map).length);
        setCustomerNames(map);
        setCustomerEmails(emailMap);
      })
      .catch(e => console.error('[customerNames] contacts fetch failed', e));
  }, [isOpen]);

  // Ask Brevo what happened to the "Payment Received" email of each approved payment on screen.
  // Matched by customer email + time (the receipt has no invoice number): a card payment's receipt goes
  // out within minutes; an ACH payment is recorded in Zoho later (settlement sync), so allow days.
  useEffect(() => {
    const MINUTE = 60 * 1000;
    const items = transactions
      .map(tx => {
        const key = String(tx['transactionId'] ?? tx['id'] ?? '');
        const email = customerEmails[String(tx['customerCode'] ?? '')];
        const raw = tx['dateCreated'] ?? tx['dateTime'] ?? tx['date'];
        const time = raw ? new Date(String(raw)).getTime() : 0;
        const isAch = Boolean(tx['bankAccountId'] || tx['bankAccountL4L4']);
        return { key, email, time, windowMs: isAch ? 4 * 24 * 60 * MINUTE : 90 * MINUTE, approved: resolveStatus(tx) === 'approved' };
      })
      .filter(i => i.approved && i.key && i.email && i.time > 0 && !receiptAskedRef.current.has(i.key));
    if (items.length === 0) return;
    items.forEach(i => receiptAskedRef.current.add(i.key));
    getBrevoReceiptStatuses(items.map(({ key, email, time, windowMs }) => ({ key, email: email as string, time, windowMs })))
      .then(found => setReceiptStatuses(prev => ({ ...prev, ...found })))
      .catch(err => {
        console.warn('Brevo receipt status lookup failed:', err);
        items.forEach(i => receiptAskedRef.current.delete(i.key)); // let a later render retry
      });
  }, [transactions, customerEmails]);

  const updateOverlayPosition = useCallback(() => {
    applySearchOverlayPosition(overlayRef.current, isSearchVisible);
  }, [isSearchVisible]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  useEffect(() => {
    let showH: { remove: () => Promise<void> } | null = null;
    let hideH: { remove: () => Promise<void> } | null = null;
    let bottomAnimRaf = 0;
    let currentBottom = 0;

    const setBottomOffset = (offset: number) => {
      const el = overlayRef.current;
      if (!el) return;
      if (offset > 0) {
        el.style.setProperty('bottom', `${offset}px`, 'important');
        el.style.removeProperty('transform');
      } else {
        el.style.removeProperty('bottom');
        el.style.removeProperty('transform');
      }
    };

    const animateBottomTo = (h: number) => {
      const targetBottom = h > 0 ? h + KEYBOARD_GAP_PX : 0;
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);

      const startBottom = currentBottom;
      const delta = targetBottom - startBottom;
      if (Math.abs(delta) < 1) {
        currentBottom = targetBottom;
        setBottomOffset(currentBottom);
        return;
      }

      const duration = targetBottom > startBottom ? 220 : 170;
      const startTime = performance.now();
      const step = (now: number) => {
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        currentBottom = startBottom + (delta * eased);
        setBottomOffset(currentBottom);
        if (t < 1) bottomAnimRaf = requestAnimationFrame(step);
        else {
          currentBottom = targetBottom;
          setBottomOffset(currentBottom);
        }
      };
      bottomAnimRaf = requestAnimationFrame(step);
    };

    const applyKb = (h: number) => {
      animateBottomTo(h);
    };
    let raf = 0;
    const vv = window.visualViewport;
    const onVv = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!vv) return;
        const off = window.innerHeight - vv.height - vv.offsetTop;
        applyKb(off > 50 ? off : 0);
      });
    };
    try {
      Keyboard.addListener('keyboardWillShow', i => applyKb(i.keyboardHeight || 0)).then(h => { showH = h; }).catch(() => { });
      Keyboard.addListener('keyboardDidShow', i => applyKb(i.keyboardHeight || 0)).catch(() => { });
      Keyboard.addListener('keyboardWillHide', () => applyKb(0)).then(h => { hideH = h; }).catch(() => { });
      Keyboard.addListener('keyboardDidHide', () => applyKb(0)).catch(() => { });
    } catch { }
    if (vv) vv.addEventListener('resize', onVv);
    return () => {
      const el = overlayRef.current;
      if (el) { el.style.removeProperty('transform'); el.style.removeProperty('bottom'); }
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);
      if (raf) cancelAnimationFrame(raf);
      if (vv) vv.removeEventListener('resize', onVv);
      try { showH?.remove(); } catch { }
      try { hideH?.remove(); } catch { }
    };
  }, []);

  const handleToggleSearch = () => {
    if (isSearchVisible) {
      setSearchText('');
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => { setIsSearchHidden(true); setIsSearchClosing(false); }, 220);
      try {
        const s = searchRef.current as HTMLIonSearchbarElement | null;
        const g = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
        if (g) g().then(el => { try { el.blur(); } catch { } try { Keyboard.hide(); } catch { } }).catch(() => { try { Keyboard.hide(); } catch { } });
        else { try { Keyboard.hide(); } catch { } }
      } catch { }
    } else {
      setIsSearchHidden(false);
      setIsSearchVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        try {
          const s = searchRef.current as HTMLIonSearchbarElement | null;
          const g = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
          if (g) g().then(el => { try { el.focus(); } catch { } }).catch(() => { });
        } catch { }
      }));
    }
  };

  const shell = (
    <div className={`${styles.modalWrapper} ${styles.page}`}>
        <BasePageWithHeader
          title={tr('components.HelcimPaymentsModal.payments')}
          toolbarClassName={styles.toolbar}
          smallTitleClassName={styles.smallTitle}
          titleHeaderClassName={styles.titleHeader}
          startButtons={
            <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
              <IonIcon icon={chevronBackOutline} />
            </IonButton>
          }
          endButtons={
            <IonButton shape="round" className={styles.searchButton} onClick={handleToggleSearch}>
              <IonIcon icon={isSearchVisible ? closeOutline : searchOutline} />
            </IonButton>
          }
        >
          {/* Filter bar — 2 pills, each opening its OWN independent sheet:
              "Select Dates" -> DateSheet (quick-picks/inputs/calendar only,
              no accordion); "Filters" -> FilterSheet's Amount/Status/Method/
              Type accordion (Date is not one of its rows). */}
          <div className={styles.filterBar}>
            <button
              className={`${styles.periodBtn} ${dateRange !== 'all' ? styles.periodBtnActive : ''}`}
              onClick={() => {
                setDraft({ amountMin, amountMax, dateRange, customDateStart, customDateEnd, statusFilter, methodFilter, typeFilter });
                setIsDateSheetOpen(true);
              }}
            >
              <IonIcon icon={calendarOutline} />{tr('components.HelcimPaymentsModal.selectDates')}
            </button>

            <button
              className={`${styles.periodBtn} ${(amountMin || amountMax || statusFilter.size > 0 || methodFilter.size > 0 || typeFilter.size > 0) ? styles.periodBtnActive : ''}`}
              onClick={() => {
                setDraft({ amountMin, amountMax, dateRange, customDateStart, customDateEnd, statusFilter, methodFilter, typeFilter });
                setExpandedCategory(null);
                setIsFilterSheetOpen(true);
              }}
            >
              <IonIcon icon={filterOutline} />{tr('components.HelcimPaymentsModal.filters')}
            </button>
          </div>

          <DateSheet
            isOpen={isDateSheetOpen}
            draft={draft}
            setDraft={setDraft}
            onClose={() => setIsDateSheetOpen(false)}
            onApply={() => {
              setDateRange(draft.dateRange);
              setCustomDateStart(draft.customDateStart);
              setCustomDateEnd(draft.customDateEnd);
              setIsDateSheetOpen(false);
            }}
            onReset={() => {
              setDateRange('all');
              setCustomDateStart('');
              setCustomDateEnd('');
              setIsDateSheetOpen(false);
            }}
          />

          <FilterSheet
            isOpen={isFilterSheetOpen}
            expandedCategory={expandedCategory}
            setExpandedCategory={setExpandedCategory}
            draft={draft}
            setDraft={setDraft}
            onClose={() => setIsFilterSheetOpen(false)}
            onApply={() => {
              setAmountMin(draft.amountMin);
              setAmountMax(draft.amountMax);
              setStatusFilter(draft.statusFilter);
              setMethodFilter(draft.methodFilter);
              setTypeFilter(draft.typeFilter);
              setIsFilterSheetOpen(false);
            }}
            onReset={() => {
              setAmountMin('');
              setAmountMax('');
              setStatusFilter(new Set());
              setMethodFilter(new Set());
              setTypeFilter(new Set());
              setIsFilterSheetOpen(false);
            }}
          />

          <IonContent ref={ionContentRef} className={styles.content}>
            <PositionedRefresher
              titleRef={titleCardRef}
              contentRef={scrollRef}
              offset={0}
              anchor="bottom"
              id="payments-refresher"
              pullMin={120}
              pullMax={200}
              pullFactor={0.6}
              disabled={refresherDisabled}
              onIonRefresh={(e) => {
                loadTransactions().finally(() => (e.detail as { complete: () => void }).complete());
              }}
              style={{
                '--ion-color-step-250': 'var(--ion-background-color)',
                '--ion-color-step-200': 'var(--ion-background-color)',
                '--ion-background-color': 'transparent',
              } as React.CSSProperties}
            >
              <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
            </PositionedRefresher>

            <IonGrid
              ref={scrollRef}
              className={styles.txContainer}
              onScroll={(e) => {
                const target = e.target as HTMLElement;
                const top = target.scrollTop || 0;
                const atTop = top <= 5;
                if (atTop !== wasAtTop.current) {
                  wasAtTop.current = atTop;
                  setRefresherDisabled(!atTop);
                }
              }}
            >
              <div ref={titleCardRef} />

              {loading && [0, 1, 2, 3].map(i => (
                <div key={`skeleton-${i}`} className={`${styles.card} ${styles.cardApproved}`} aria-hidden="true">
                  <div className={styles.cardTop}>
                    <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '45%', height: 20, margin: 0 }} />
                    <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 80, height: 22, margin: 0, borderRadius: 11 }} />
                  </div>
                  <div className={styles.statsRow}>
                    {[0, 1, 2].map(j => (
                      <React.Fragment key={j}>
                        {j > 0 && <div className={styles.statDivider} />}
                        <div className={styles.stat}>
                          <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 28, height: 28, margin: 0, borderRadius: 6 }} />
                          <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '70%', height: 16, margin: '8px 0 0' }} />
                          <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '50%', height: 12, margin: '6px 0 0' }} />
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                  <div className={styles.cardFooter}>
                    <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 110, height: 18, margin: 0, borderRadius: 9 }} />
                    <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 130, height: 18, margin: 0, borderRadius: 9 }} />
                  </div>
                </div>
              ))}

              {!loading && error && (
                <div className={styles.center}>
                  <IonText color="danger"><p>{error}</p></IonText>
                  <IonButton fill="outline" size="small" onClick={loadTransactions}>{tr('components.HelcimPaymentsModal.retry')}</IonButton>
                </div>
              )}

              {!loading && !error && filtered.length === 0 && (
                <div className={styles.center}>
                  <IonIcon icon={cashOutline} style={{ fontSize: 48, color: 'var(--ion-color-medium)' }} />
                  <IonText color="medium"><p>{tr('components.HelcimPaymentsModal.noTransactionsFound')}</p></IonText>
                </div>
              )}

              {!loading && filtered.map((tx, i) => {
                const status = resolveStatus(tx);
                const txType = String(tx['type'] ?? 'purchase');
                const amount = Number(tx['amount'] ?? tx['totalAmount'] ?? 0);
                const currency = String(tx['currency'] ?? 'CAD');
                const customerCode = String(tx['customerCode'] ?? '—');
                const cardHolderName = String(tx['cardHolderName'] ?? '');
                const cardBrand = pick(tx, 'cardType', 'card_type', 'cardBrand', 'card_brand', 'brand', 'scheme');
                const dateCreated = tx['dateCreated'] ?? tx['dateTime'] ?? tx['date'];
                const invoiceNum = String(tx['invoiceNumber'] ?? tx['invoice_number'] ?? '');

                // Never falls back to cardHolderName — that's whoever's card was
                // swiped, not necessarily the actual customer (e.g. a spouse can
                // pay someone else's invoice), so it's not a reliable identity.
                const displayName = customerNames[customerCode] || customerCode;
                const cardNumber = formatCardNumber(tx);
                const bankL4 = String(tx['bankAccountL4L4'] ?? '').trim();
                const isAch = Boolean(tx['bankAccountId'] || tx['bankAccountL4L4']);

                const receiptState = brevoEmailState(receiptStatuses[String(tx['transactionId'] ?? tx['id'] ?? '')], {}, k => tr(k));

                const cardClass =
                  status === 'approved' ? styles.cardApproved :
                  status === 'declined' ? styles.cardDeclined :
                  status === 'voided' ? styles.cardRefunded :
                  ['refunded', 'reversed'].includes(status) ? styles.cardRefunded : '';

                return (
                  <div
                    key={String(tx['transactionId'] ?? tx['id'] ?? i)}
                    className={`${styles.card} ${cardClass}`}
                    onClick={() => setSelectedTx(tx)}
                  >
                    <div className={styles.cardTop}>
                      <div className={styles.cardName}>{displayName}</div>
                      <IonBadge
                        className={`${styles.badge} ${styles[BADGE_TONE_CLASS[STATUS_COLOR[status] ?? 'medium']]}`}
                      >
                        {translateDynamic(status === 'pending' ? 'processing' : status) || '—'}
                      </IonBadge>
                    </div>
                    <div className={styles.statsRow}>
                      <div className={styles.stat}>
                        <IonIcon icon={cashOutline} className={`${styles.statIcon} ${styles.statIconGreen}`} />
                        <span className={styles.statValue}>{fmtCurrency(amount, currency)}</span>
                        <span className={styles.statLabel}>{txTypeLabel(txType)}</span>
                      </div>
                      <div className={styles.statDivider} />
                      <div className={`${styles.stat} ${styles.statCard}`}>
                        {isAch ? (
                          <>
                            <BankLogo />
                            <span className={styles.cardNumber}>
                              {bankL4 ? tr('components.HelcimPaymentsModal.bank2', { bankL4 }) : '—'}
                            </span>
                          </>
                        ) : (
                          <>
                            <CardBrandLogo brand={cardBrand} />
                            {cardNumber !== '—' ? (
                              <div className={styles.cardNumberWrap}>
                                <span className={styles.cardNumPrefix}>{cardNumber.slice(0, -4)}</span>
                                <span className={styles.cardNumSuffix}>{cardNumber.slice(-4)}</span>
                              </div>
                            ) : (
                              <span className={styles.cardNumber}>—</span>
                            )}
                          </>
                        )}
                        {cardHolderName && <span className={styles.statSubLabel}>{cardHolderName}</span>}
                      </div>
                      <div className={styles.statDivider} />
                      <div className={styles.stat}>
                        <IonIcon icon={calendarOutline} className={`${styles.statIcon} ${styles.statIconBlue}`} />
                        <span className={styles.statValue}>{fmtDate(dateCreated)}</span>
                        <span className={styles.statLabel}>{tr('components.HelcimPaymentsModal.date')}</span>
                      </div>
                    </div>
                    <div className={styles.cardFooter}>
                      <span className={styles.footerItem}>{tr('components.HelcimPaymentsModal.method')} {METHOD_OPTION_LABELS[isAch ? 'ach' : 'card']}</span>
                      {invoiceNum && <span className={styles.footerItem}>{tr('components.HelcimPaymentsModal.inv', { invoiceNum })}</span>}
                      {receiptState && (
                        <span className={`${styles.footerItem} ${styles.footerItemReceipt}`} title={receiptState.label} style={{ color: receiptState.color }}>
                          <IonIcon icon={receiptState.icon} />
                          {tr('components.HelcimPaymentsModal.receipt')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {!loading && loadingMore && (
                <div className={styles.center}>
                  <IonSpinner name="bubbles" color="primary" />
                </div>
              )}

              {isSearchVisible && <div className={styles.searchBarSpacer} />}
            </IonGrid>
          </IonContent>

          {/* Morphing search overlay — outside IonContent, inside BasePageWithHeader */}
          <IonFab
            ref={el => { overlayRef.current = el; }}
            className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`}
            aria-hidden={!isSearchVisible}
          >
            <IonSearchbar
              ref={searchRef}
              value={searchText}
              debounce={50}
              onIonInput={e => setSearchText(e.detail.value ?? '')}
              onIonClear={() => setSearchText('')}
              placeholder={tr('components.HelcimPaymentsModal.searchTransactions', { length: transactions.length })}
              animated
              showCancelButton="never"
              showClearButton={searchText ? 'always' : 'never'}
              className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
            />
          </IonFab>
        </BasePageWithHeader>
      </div>

  );

  return (
    <>
      {asPage ? (
        <IonPage className={styles.page}>{shell}</IonPage>
      ) : (
        <IonModal isOpen={isOpen} onDidDismiss={onClose} className={styles.modal}>
          {shell}
        </IonModal>
      )}

      <HelcimTransactionDetailModal
        tx={selectedTx}
        onClose={() => setSelectedTx(null)}
        onInvoiceClick={handleInvoiceClick}
        customerNames={customerNames}
      />

      <IonModal
        isOpen={!!invoiceIdToOpen}
        onDidDismiss={() => setInvoiceIdToOpen(null)}
        handle={false}
      >
        {invoiceIdToOpen && (
          <Invoice
            invoiceId={invoiceIdToOpen}
            onClose={() => setInvoiceIdToOpen(null)}
          />
        )}
      </IonModal>
    </>
  );
};

export default HelcimPaymentsModal;
